/**
 * KinsCircle Phase 6C — Semantic Family Memory Intelligence flag layer.
 *
 * STRICTLY ADDITIVE. ALL FLAGS DEFAULT TO FALSE.
 * Honors STABILITY.md §1, §3, §11, §12, §13.
 *
 * When a flag is FALSE:
 *   - all semantic helpers early-return (no work, no errors)
 *   - all UI components render `null`
 *   - no localStorage writes are performed
 *   - existing app behavior is byte-identical
 *
 * Resolution order (first match wins) for every flag:
 *   1. window.__KINSCIRCLE_SEMANTIC_*__               (runtime override)
 *   2. localStorage 'kinscircle.semantic.*.enabled'   (persistent override)
 *   3. import.meta.env.VITE_ENABLE_SEMANTIC_*         (build-time)
 *   4. false                                          (default)
 *
 * NOTE: Phase 6C is read-only. None of these flags grant write access to
 * any family-graph table. They control which discovery surfaces appear in
 * the UI and which in-memory indexes are built.
 */

type SemanticFlagKey =
  | 'semanticIndex'
  | 'smartSearch'
  | 'memoryClusters'
  | 'historianMode'
  | 'relationshipInsights'
  | 'memoryResurfacing';

type SemanticFlagSpec = {
  windowKey: string;
  lsKey: string;
  envKey: string;
};

const SEMANTIC_SPECS: Record<SemanticFlagKey, SemanticFlagSpec> = {
  semanticIndex: {
    windowKey: '__KINSCIRCLE_SEMANTIC_INDEX__',
    lsKey: 'kinscircle.semantic.semanticIndex.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_INDEX',
  },
  smartSearch: {
    windowKey: '__KINSCIRCLE_SEMANTIC_SMART_SEARCH__',
    lsKey: 'kinscircle.semantic.smartSearch.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_SMART_SEARCH',
  },
  memoryClusters: {
    windowKey: '__KINSCIRCLE_SEMANTIC_MEMORY_CLUSTERS__',
    lsKey: 'kinscircle.semantic.memoryClusters.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_MEMORY_CLUSTERS',
  },
  historianMode: {
    windowKey: '__KINSCIRCLE_SEMANTIC_HISTORIAN__',
    lsKey: 'kinscircle.semantic.historianMode.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_HISTORIAN',
  },
  relationshipInsights: {
    windowKey: '__KINSCIRCLE_SEMANTIC_RELATIONSHIP_INSIGHTS__',
    lsKey: 'kinscircle.semantic.relationshipInsights.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_RELATIONSHIP_INSIGHTS',
  },
  memoryResurfacing: {
    windowKey: '__KINSCIRCLE_SEMANTIC_RESURFACING__',
    lsKey: 'kinscircle.semantic.memoryResurfacing.enabled',
    envKey: 'VITE_ENABLE_SEMANTIC_RESURFACING',
  },
};

const readWindow = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(window as any)[key];
  } catch {
    return false;
  }
};

const readLocal = (key: string): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const readEnv = (key: string): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env ?? {};
    const v = env[key];
    if (typeof v === 'string') return v === 'true' || v === '1';
    if (typeof v === 'boolean') return v;
  } catch {
    /* non-fatal */
  }
  return false;
};

const semanticFlagEnabled = (k: SemanticFlagKey): boolean => {
  const s = SEMANTIC_SPECS[k];
  if (readWindow(s.windowKey)) return true;
  if (readLocal(s.lsKey)) return true;
  if (readEnv(s.envKey)) return true;
  return false;
};

// ── Public API ──────────────────────────────────────────────────────────────

export const isSemanticIndexEnabled = (): boolean =>
  semanticFlagEnabled('semanticIndex');

export const isSmartSearchEnabled = (): boolean =>
  // Smart search requires the underlying index to be enabled.
  semanticFlagEnabled('semanticIndex') && semanticFlagEnabled('smartSearch');

export const isMemoryClustersEnabled = (): boolean =>
  semanticFlagEnabled('semanticIndex') && semanticFlagEnabled('memoryClusters');

export const isHistorianModeEnabled = (): boolean =>
  semanticFlagEnabled('semanticIndex') && semanticFlagEnabled('historianMode');

export const isRelationshipInsightsEnabled = (): boolean =>
  semanticFlagEnabled('semanticIndex') &&
  semanticFlagEnabled('relationshipInsights');

export const isMemoryResurfacingEnabled = (): boolean =>
  semanticFlagEnabled('semanticIndex') &&
  semanticFlagEnabled('memoryResurfacing');

/** QA / dev override — flips the persistent localStorage flag. */
export const setSemanticFlagLocal = (
  flag: SemanticFlagKey,
  enabled: boolean,
): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const k = SEMANTIC_SPECS[flag].lsKey;
    if (enabled) window.localStorage.setItem(k, '1');
    else window.localStorage.removeItem(k);
  } catch {
    /* non-fatal */
  }
};

/**
 * Convenience: snapshot of the current semantic flag state for the
 * owner/admin diagnostics surface.
 */
export const snapshotSemanticFlags = (): Record<SemanticFlagKey, boolean> => ({
  semanticIndex: semanticFlagEnabled('semanticIndex'),
  smartSearch: semanticFlagEnabled('smartSearch'),
  memoryClusters: semanticFlagEnabled('memoryClusters'),
  historianMode: semanticFlagEnabled('historianMode'),
  relationshipInsights: semanticFlagEnabled('relationshipInsights'),
  memoryResurfacing: semanticFlagEnabled('memoryResurfacing'),
});

export type { SemanticFlagKey };
